import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux"
import TablePagination from "./table/TablePagination";
import debounce from 'lodash.debounce';
import { Button, CloseButton, HStack, Input, InputGroup, InputLeftElement, InputRightElement, useDisclosure } from "@chakra-ui/react";
import InputField from "components/fields/InputField";
import Modal from "components/modal/Modal";
import { FaEdit, FaPencilAlt, FaPlusCircle, FaShoppingCart, FaTools, FaTrashAlt, FaUndoAlt } from "react-icons/fa";
import TooltipComponent from "components/tooltip";
import { FiSearch } from "react-icons/fi";
import { SET_TO_DEFAULT_RESPONSE, getByPageProd, getByPageProdId } from "../../../store/features/slices/kardexSlice";
import { uriSerialized } from "utils/uriSerialized";
import 'moment/locale/es';
import TablePaginationByProdId from "./table/TablePaginationByProdId";
import { FaCashRegister, FaList } from "react-icons/fa6";
import clsx from "clsx";

const Kardex = () => {
    const dispatch = useDispatch();
    const byProdId = useDisclosure()
    const { userToken } = useSelector((state) => state.userReducer);
    const {
        dataByPageProd: dataByPage,
        dataByPageProdId,
        totalPageProd: totalPage,
        totalPageProdId,
        totalRowProd: totalRow,
        totalRowProdId,
        totalProd: total,
        responseSuccessCreate,
        isLoading,
        isLoadingProdId,
        isSuccess,
        isError,
        message,
        responseSuccessGet,
        responseSuccessDelete,
        responseSuccessUpdate
    } = useSelector((state) => state.kardexReducer);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermByProId, setSearchTermByProdId] = useState('');
    const [proId, setProdId] = useState(null);
    const [selectedFiltro, setSelectedFiltro] = useState("");

    const fetchData = useCallback(
        (pageSize, pageIndex, search, order) => {
            const queryOptions = {
                page: pageIndex,
                limit: pageSize,
                search: search,
                order: order
            }
            const query = queryOptions ? "?" + uriSerialized(queryOptions) : ""
            dispatch(getByPageProd({ query: query, token: userToken }))
        },
        [responseSuccessDelete, responseSuccessCreate]
    )

    const fetchDataProdId = useCallback(
        (pageSize, pageIndex, search, order) => {
            const queryOptions = {
                page: pageIndex,
                limit: pageSize,
                search: search,
                order: order,
                ...(proId?._id && { prodId: proId?._id })
            }
            const query = queryOptions ? "?" + uriSerialized(queryOptions) : ""
            dispatch(getByPageProdId({ query: query }))
        },
        [proId, dispatch]
    )

    const handleOpenProdId = (prod) => {
        setSearchTermByProdId("")
        setSelectedFiltro("")
        setProdId(prod.producto)
        byProdId.onOpen()
    }

    const onChangeSearchTerm = e => {
        setSearchTerm(e.target.value);
    };

    const onChangeSearchTermClear = e => {
        setSearchTerm("");
        document.getElementById("search").value = "";
    };

    const debounceSearchTerm = useMemo(() => debounce(onChangeSearchTerm, 700), []);

    const filtros = [
        { label: "Compras", value: "compra", colorScheme: "green", icon: <FaShoppingCart /> },
        { label: "Ventas", value: "venta", colorScheme: "blue", icon: <FaCashRegister /> },
        { label: "Ajustes", value: "ajuste", colorScheme: "orange", icon: <FaTools /> },
        { label: "Devoluciones", value: "devolucion", colorScheme: "purple", icon: <FaUndoAlt /> },
        { label: "Todos", value: "", colorScheme: "gray", icon: <FaList /> },
    ];

    return (
        <div className="mt-4 md:mt-0">
            {/** KADEX BY PROD ID */}
            <Modal isOpen={byProdId.isOpen} onClose={byProdId.onClose} headerTitle={`Kardex de ${proId?.name}`}>
                <div className="px-[5px] md:px-[10px] pt-[20px] pb-[20px] max-w-[1368px] flex flex-col !z-[1004]">
                    <HStack spacing={3} wrap="wrap">
                        {filtros.map((filtro) => {
                            const isSelected = selectedFiltro === filtro.value;

                            const colorMap = {
                                green: {
                                    border: "border-green-500 dark:border-green-400",
                                    ring: "ring-green-400 dark:ring-green-500",
                                    bg: "!bg-gradient-to-r from-green-700 to-green-900 dark:from-green-700 dark:to-green-900"
                                },
                                blue: {
                                    border: "border-blue-500 dark:border-blue-400",
                                    ring: "ring-blue-400 dark:ring-blue-500",
                                    bg: "!bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-700 dark:to-blue-900"
                                },
                                purple: {
                                    border: "border-purple-500 dark:border-purple-400",
                                    ring: "ring-purple-400 dark:ring-purple-500",
                                    bg: "!bg-gradient-to-r from-purple-700 to-purple-900 dark:from-purple-700 dark:to-purple-900"
                                },
                                orange: {
                                    border: "border-orange-500 dark:border-orange-400",
                                    ring: "ring-orange-400 dark:ring-orange-500",
                                    bg: "!bg-gradient-to-r from-orange-700 to-orange-900 dark:from-orange-700 dark:to-orange-900"
                                },
                                gray: {
                                    border: "border-gray-500 dark:border-gray-400",
                                    ring: "ring-gray-400 dark:ring-gray-500",
                                    bg: "!bg-gradient-to-r from-gray-700 to-gray-900 dark:from-gray-700 dark:to-gray-900"
                                },
                            };

                            const bgColorMap = {
                                green: "!bg-gradient-to-r from-green-500 to-green-700 dark:from-green-500 dark:to-green-700 !text-white gap-2 !font-normal",
                                blue: "!bg-gradient-to-r from-blue-500 to-blue-700 dark:from-blue-500 dark:to-blue-700 !text-white gap-2 !font-normal",
                                purple: "!bg-gradient-to-r from-purple-500 to-purple-700 dark:from-purple-500 dark:to-purple-700 !text-white gap-2 !font-normal",
                                orange: "!bg-gradient-to-r from-orange-500 to-orange-700 dark:from-orange-500 dark:to-orange-700 !text-white gap-2 !font-normal",
                                gray: "!bg-gradient-to-r from-gray-500 to-gray-700 dark:from-gray-500 dark:to-gray-700 !text-white gap-2 !font-normal",
                            };


                            return (
                                <Button
                                    key={filtro.value}
                                    disabled={isLoadingProdId}
                                    onClick={() => {
                                        setSearchTermByProdId(filtro.value);
                                        setSelectedFiltro(filtro.value);
                                    }}
                                    className={`!border-2 !rounded-2xl ${bgColorMap[filtro.colorScheme]} ${isSelected ? colorMap[filtro.colorScheme].border + ' ' + colorMap[filtro.colorScheme].ring + ' ' + colorMap[filtro.colorScheme].bg : '!border-none !ring-transparent !ring-none'}`}
                                >
                                    {filtro.icon}
                                    {filtro.label}
                                </Button>
                            );
                        })}
                    </HStack>
                    <div className="grid h-full grid-cols-1 !min-h-[280px] mt-5">
                        <TablePaginationByProdId
                            searchTerm={searchTermByProId}
                            tableData={dataByPageProdId}
                            fetchData={fetchDataProdId}
                            loading={isLoadingProdId}
                            pageCount={totalPageProdId}
                            totalRow={totalRowProdId}
                        />
                    </div>
                </div>
            </Modal>

            <div className="sticky z-30 w-full top-44 md:top-28 flex items-center justify-end bg-white dark:!bg-navy-800 p-2 rounded-xl shadow-xl shadow-gray-200 dark:shadow-none">
                <div className="relative flex h-[61px] w-[calc(100%-55px)] flex-grow items-center gap-2 rounded-full bg-white px-0 py-2 dark:!bg-navy-800">
                    <InputGroup className="!w-full md:!w-[300px]">
                        <InputLeftElement pointerEvents="none" className="!mt-1 !ml-1">
                            <FiSearch size={24} className="text-navy-500 dark:text-navy-300" />
                        </InputLeftElement>
                        <Input size='lg' autoFocus id="search" onChange={debounceSearchTerm} type="text" placeholder={`Buscar ${totalRow} productos...`} className="flex w-full items-center justify-center !rounded-xl border !bg-lightPrimary 
            dark:!bg-navy-900 !text-sm !ring-none focus:!ring-brand-500 
            focus:!border-brand-500 dark:focus:!ring-brand-400 dark:focus:!border-brand-400 
            !outline-none !border-gray-200 dark:!border-white/10 dark:text-white" />
                        <InputRightElement className="!mt-1">
                            <CloseButton onClick={onChangeSearchTermClear} size='md' className={`mr-2 ${searchTerm.trim().length > 0 ? 'scale-100' : 'scale-0'} transition-all duration-500 text-navy-500 dark:text-navy-300`} />
                        </InputRightElement>
                    </InputGroup>
                </div>
            </div>
            <div className="mt-3 md:mt-8">

                <div className="mt-5 grid h-full grid-cols-1">
                    <TablePagination
                        searchTerm={searchTerm}
                        tableData={dataByPage}
                        fetchData={fetchData}
                        loading={isLoading}
                        pageCount={totalPage}
                        totalRow={totalRow}
                        actions={{
                            onView: (item) => handleOpenProdId(item)
                        }}
                    />
                </div>
            </div>
        </div >

    );
};

export default Kardex;
